import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { Sheep } from './sheep';
import { computeSafeSpawnPosition } from './spawnHelper';

/**
 * J.Frog (GunBound mobile). The shot is thrown in an arc; once it lands it
 * walks along the terrain (following slopes, no jumping) in a fixed
 * direction until its fuse runs out and it explodes. Type 1 walks left,
 * Type 2 walks right — the walk direction is independent of which way the
 * worm is facing, so you can land it past a target and walk back into it.
 */
const FROG_EXPLOSION_RADIUS = 45;
const FROG_MAX_DAMAGE = 45;
/** Fuse, in seconds. Only starts counting once the frog lands and crawls. */
const FROG_LIFETIME = 2;

function makeFrog(id: string, name: string, walkDirection: 'left' | 'right'): WeaponDefinition {
  return {
    id,
    name,
    icon: `${id}.png`,
    category: 'thrown',
    maxAmmo: 2,
    maxUsesPerTurn: 1,
    powerCharging: true,
    timerAdjustable: false,
    showAimGuide: true,
    allowRetreat: true,

    fire(ctx: WeaponContext): WeaponResult {
      const { worm, angle, power, world } = ctx;

      const maxSpeed = 750;
      const speed = (power / 100) * maxSpeed;
      const rad = (angle * Math.PI) / 180;
      const aimRad = worm.facing === 'right' ? rad : Math.PI - rad;

      const spawn = computeSafeSpawnPosition(worm, aimRad, 20, world);

      // Thrown along the aim; walks the weapon's fixed direction on landing.
      const frog = new Sheep(spawn.x, spawn.y, walkDirection, worm.teamId, {
        kind: 'frog',
        launchVx: Math.cos(aimRad) * speed,
        launchVy: -Math.sin(aimRad) * speed,
        lifetime: FROG_LIFETIME,
        explosionRadius: FROG_EXPLOSION_RADIUS,
        maxDamage: FROG_MAX_DAMAGE,
      });

      return {
        success: true,
        entities: [frog],
        message: 'Ribbit!',
      };
    },
  };
}

/** Type 1: walks left after landing. */
export const WeaponFrogLeft = makeFrog('frogLeft', 'Frog ◄', 'left');
/** Type 2: walks right after landing. */
export const WeaponFrogRight = makeFrog('frogRight', 'Frog ►', 'right');
